import './ReportScreen.scss';

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Helmet } from 'react-helmet';
import DataTable from 'src/component/UI/DataTable';
import { Map, List } from 'immutable';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import SendReportIcon from '@mui/icons-material/Send';
import TextField from '@mui/material/TextField';

import Button from 'src/component/UI/Button';
import EditableCell from 'src/component/UI/EditableCell';

import { fetchAlarmStats } from 'src/module/alarm/action';
import { sendReport } from 'src/module/facility/action';

import { fetchNewWorkTickets, updateWorkTicket } from 'src/module/workTicket/action';

import { getNewWorkTickets } from 'src/module/workTicket/selector';
import { fetchClient } from 'src/module/authentication/action';
import ForcedOnCircuitsList from 'src/component/UI/ForcedOnCircuits/ForcedOnCircuitsList';
import DashboardStatistics from 'src/component/UI/Statistics/DashboardStatistics';
import { getClient } from 'src/module/authentication/selector';
import { getPluralWorkTicketLabel, getSingularWorkTicketLabel } from 'src/module/authentication/selector';
import DeviceLinkCombo from 'src/component/UI/DeviceLinkCombo';
import FeatureFence from 'src/component/FeatureFence';
import { isWhitelabel } from 'src/utils/utils';
import { useTranslation } from 'src/i18n';


export default function ReportScreen () {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [reportSent, setReportSent] = useState(false);
  const [message, setMessage] = useState('');

  const newWorkTickets = useSelector(getNewWorkTickets) as any;
  const pluralWorkTicketLabel = useSelector(getPluralWorkTicketLabel);
  const singularWorkTicketLabel = useSelector(getSingularWorkTicketLabel);

  const client = useSelector(getClient);
  const lastReportSentAt = client.get('last_report_sent_at');

  useEffect(() => {
    dispatch(fetchClient());
    dispatch(fetchAlarmStats(Map(), Map()));
    dispatch(fetchNewWorkTickets(lastReportSentAt));
  }, [lastReportSentAt]);

  const handleUpdateRow = (field: string, sapNotificationId: string) => {
    return (val) => {
      const rec: any = {};
      rec[field] = val;
      dispatch(updateWorkTicket(sapNotificationId, Map(rec), null));

    };
  };

  const handleSendReport = () => {
    if (!reportSent) {
      setReportSent(true);

      dispatch(sendReport(message));
    }
  };

  const notificationData = newWorkTickets.reduce((acc, notification) => {
    acc = acc.push(Map({
      sapNotificationNum: notification.get('sap_notification_id'),
      device_tag: notification.get('device_tag'),
      parent_tag: notification.get('parent_tag'),
      device_type: notification.get('device_type'),
      parent_type: notification.get('parent_type'),
      note: notification.get('note') || '',
      description: notification.get('description') || '',
      revision: notification.get('revision') || '',
      sapNotificationId: notification.get('id') || 'N/A',
      circuit_load_current: notification.get('circuit_load_current')
    }));
    return acc;
  }, List());

  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';

  return (
    <div className='page' id='report'>
      <Helmet>
        <title>{title} - {t('reports.sendReport')}</title>
      </Helmet>

      <h3>{t('reports.sendReport')}</h3>


      <DashboardStatistics layoutPreferencePath='client.preferences.report.layout' defaultLayoutOverride={[
        { "type": "grid", "container": true, "spacing": 3, "children": [
          { "type": "grid", "container": false, "md": 4, "xs": 12, "children": [
            { "type": "block", "title": "Report Information", "children": [
              { "type": "minor", "title": "Date", "value": "date" },
              { "type": "minor", "title": "Last Report Date", "value": "lastReportSentAt" },
              { "type": "minor", "title": "Week", "value": "weekNumber" },
              { "type": "minor", "title": "Temperature", "value": "currentSiteTemperature", "defaultValue": "N/A" }
            ] }
          ] },
          { "type": "grid", "container": false, "md": 4, "xs": 12, "children": [
            { "type": "block", "title": "Alarm Overview", "children": [
              { "type": "minor", "title": "Total Alarms", "value": "alarmStats.totalAlarms", "helpText": "Total active alarms in the system", "defaultValue": "N/A" },
              { "type": "minor", "title": "HTCs in Crit. Alarm", "value": "alarmStats.criticalAlarms", "helpText": "Total Heat Trace Controllers in critical alarm", "defaultValue": "N/A" },
              { "type": "minor", "title": "%mnraLabel%", "value": "alarmStats.outstandingNotificationsByStatus.mnra", "helpText": "Total open SAP tickets requiring engineering", "defaultValue": "N/A" },
              { "type": "minor", "title": "%workOrderLabel%", "value": "alarmStats.outstandingNotificationsByStatus.wo", "helpText": "Total open SAP notifications for an approved minor repair", "defaultValue": "N/A" }
            ] }
          ] },
          { "type": "grid", "container": false, "md": 4, "xs": 12, "children": [
            { "type": "block", "title": "System Overview", "children": [
              { "type": "minor", "title": "Critical Alarms w/o Work Ticket", "value": "criticalAlarmsWithoutNotification", "helpText": "Total number of devices in critical alarm and no associated work ticket" },
              { "type": "minor", "title": "Total Forced On", "value": "alarmStats.forcedOnStats.totalForcedOnCircuits", "defaultValue": "N/A" },
              { "type": "minor", "title": "Total LOTO", "value": "circuitsLoto", "helpText": "Total number of alarms resulting from device being Locked Out / Tagged Out", "defaultValue": "N/A" },
              { "type": "minor", "title": "Total Nuisance", "value": "alarmStats.notesByHtc.nuisance", "helpText": "Total number of alarms determined to be a nuisance alarm", "defaultValue": "N/A" }
            ] }
          ] }
        ] }
      ]} />

      <Grid container spacing={2} >
        <Grid item xs={12} md={12} sx={{ marginTop: '16px' }}>
          <Paper>
            <h3>{t('report.message')}</h3>
            <TextField
              name='message'
              helperText={t('report.messageHelperText')}
              variant='outlined'
              fullWidth
              value={message}
              onChange={e => setMessage(e.target.value)}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={12}>
          <Paper>
            <h2>{t('workTickets.newCreated', { label: pluralWorkTicketLabel || t('workTickets.title') })}</h2>
            <DataTable
              columns={[
                {
                  Header: t('workTickets.ticketNo', { label: singularWorkTicketLabel || t('workTickets.singular') }),
                  accessor: (row: any) => row.get('sapNotificationNum'),
                },
                {
                  Header: t('report.tag'),
                  accessor: (row: any) => row.get('device_tag'),
                  Cell: (row: any) => { return (<DeviceLinkCombo device={row.cell.row.original} /> );},
                },
                {
                  Header: t('common.description'),
                  accessor: (row: any) => row.get('description'),
                  Cell: (row: any) => { return (<EditableCell className='description-cell' onChange={handleUpdateRow('description', row.cell.row.original.get('sapNotificationId'))} value={row.cell.value} emptyCellLabel={t('report.addDescription')} />); },
                },
                {
                  Header: t('report.revision'),
                  accessor: (row: any) => row.get('revision'),
                  Cell: (row: any) => { return (<EditableCell className='revision-cell' onChange={handleUpdateRow('revision', row.cell.row.original.get('sapNotificationId'))} value={row.cell.value} emptyCellLabel={t('report.addRevision')} />); },
                },
                {
                  Header: t('report.notes'),
                  accessor: (row: any) => row.get('notes'),
                  Cell: (row: any) => { return (<EditableCell className='note-cell' onChange={handleUpdateRow('note', row.cell.row.original.get('sapNotificationId'))} value={row.cell.value} emptyCellLabel={t('report.addNote')} />); },
                },
                {
                  Header: t('report.load'),
                  accessor: (row: any) => row.get('circuit_load_current'),
                  Cell: (row: any) => {
                    const load = row.cell.value;
                    return load != null ? `${load.toFixed(2)} A` : '-';
                  },
                }
              ]}
              data={notificationData}
              tableId='reportScreenTable'
            />
          </Paper>
        </Grid>

        <FeatureFence can={['htc-stats']}>
          <Grid item xs={12} md={12}>
            <Paper>
              <ForcedOnCircuitsList
                title='Forced-on Circuits Past Due'
                filterable={false}
                pagination={false}
                showArchivedOption={false}
                supressSelectedFilters
                expiredOnly />
            </Paper>
          </Grid>
        </FeatureFence>
      </Grid>

      <Grid item xs={12} md={12}>
        <div className='button-bar'>
          <Button disabled={reportSent} cta onClick={handleSendReport} icon={<SendReportIcon style={{ fontSize: 14 }} />} >{t('report.sendReport')}</Button>
        </div>
      </Grid>

    </div>
  );
}
